// Veto links shown after a match is created with veto (Create Match page). Same look as the veto page
// (veto/veto.css tokens): one card per team captain in the team's colour (team 1 blue, team 2 orange),
// then the admin link (acts for both teams - keep it private) and the spectator link, and one button
// that copies all of them as a message ready for Discord.
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import TeamLogo from '../teams/TeamLogo';
import './vetoLinks.css';

export default function VetoLinksModal({ vetoLinks, team1Name, team2Name, team1 = null, team2 = null, onClose }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(null);
  const full = (k) => (vetoLinks && vetoLinks[k] ? `${window.location.origin}${vetoLinks[k]}` : null);
  const n1 = team1Name || t('createMatch.team1', 'Team 1');
  const n2 = team2Name || t('createMatch.team2', 'Team 2');

  const copy = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(c => (c === key ? null : c)), 1600);
  };
  const allText = [
    `${n1} vs ${n2} - ${t('vetoLinks.mapVeto', 'map veto')}`,
    `${n1}: ${full('team1')}`,
    `${n2}: ${full('team2')}`,
    full('spectator') && `${t('vetoLinks.spectator', 'Spectator')}: ${full('spectator')}`,
  ].filter(Boolean).join('\n');

  const Actions = ({ k }) => (
    <div className="vl-actions">
      <button type="button" className={`vl-btn${copied === k ? ' ok' : ''}`} onClick={() => copy(full(k), k)}>
        {copied === k ? `✓ ${t('vetoLinks.copied', 'Copied')}` : t('vetoLinks.copyLink', 'Copy link')}
      </button>
      <a className="vl-btn ghost" href={vetoLinks[k]} target="_blank" rel="noopener noreferrer">{t('vetoLinks.open', 'Open')} ↗</a>
    </div>
  );

  return (
    <div className="vl-backdrop" role="dialog" aria-modal="true" aria-labelledby="vl-title">
      <div className="vl">
        <header className="vl-head">
          <h2 id="vl-title">{t('vetoLinks.readyTitle', 'The veto room is ready')}</h2>
          <p>{t('vetoLinks.readyText', 'Send each captain their team’s link. The veto starts when they open it, and the match is sent to the server as soon as the veto ends.')}</p>
        </header>

        <div className="vl-teams">
          {[{ k: 'team1', name: n1, team: team1, cls: 't1' }, { k: 'team2', name: n2, team: team2, cls: 't2' }].map(x => (
            <section key={x.k} className={`vl-team ${x.cls}`}>
              <div className="vl-team-top">
                {x.team ? <TeamLogo team={x.team} size={40} radius={10} /> : <span className="vl-initial">{(x.name || '?')[0]}</span>}
                <div className="vl-team-name">
                  <strong>{x.name}</strong>
                  <span>{t('vetoLinks.captainLink', 'Captain’s link')}</span>
                </div>
              </div>
              <code className="vl-url" title={full(x.k)}>{full(x.k)}</code>
              <Actions k={x.k} />
            </section>
          ))}
        </div>

        <div className="vl-more">
          {vetoLinks.admin && (
            <div className="vl-row">
              <div className="vl-row-text">
                <strong>{t('vetoLinks.admin', 'Admin')}</strong>
                <span>{t('vetoLinks.adminHint', 'Can act for both teams and reset the veto. Keep it to yourself.')}</span>
              </div>
              <Actions k="admin" />
            </div>
          )}
          {vetoLinks.spectator && (
            <div className="vl-row">
              <div className="vl-row-text">
                <strong>{t('vetoLinks.spectator', 'Spectator')}</strong>
                <span>{t('vetoLinks.spectatorHint', 'Watch-only, for casters, the stream or anyone else.')}</span>
              </div>
              <Actions k="spectator" />
            </div>
          )}
        </div>

        <footer className="vl-foot">
          <button type="button" className={`vl-btn ghost${copied === 'all' ? ' ok' : ''}`} onClick={() => copy(allText, 'all')}>
            {copied === 'all' ? `✓ ${t('vetoLinks.copied', 'Copied')}` : t('vetoLinks.copyAll', 'Copy all links')}
          </button>
          <button type="button" className="vl-btn primary" onClick={onClose}>{t('vetoLinks.goToMatch', 'Go to the match')}</button>
        </footer>
      </div>
    </div>
  );
}
